// The price-history summary math, with no Node imports: the build (scripts/tape-summary.mjs
// re-exports it) and the edge renderer (functions/_lib/edge-page.ts) compute the exact same
// summary from the same daily recordings. See tape-summary.mjs for what it is and why.
//
// ONE COMPARABLE SERIES (clean-price-history, 2026-10-22). Every statistic here — and the catalog's
// trend and day change (scripts/gen-catalog.mjs), the World Series page's 14 days
// (scripts/gen-insights.mjs) — reads the same daily floors: the cheapest COMPARABLE, UNFLAGGED
// price of each recorded day. Comparable: a price with fees included as it is, a price before fees
// times its marketplace's fee wedge (an ESTIMATE, flagged `est`). Unflagged: not that day's phantom
// (shared/providers.mjs dayFlags). The raw cross-marketplace minimum used to mix Ticket Squeeze's
// before-fees feed prices, widget readings and phantoms: Duke-UNC read "down $757.65" for a real
// move of $123, and Bears-Lions "lowest ever seen $86.25" for a price nobody could pay.
import { comparablePrice, dayFlags, feeWedge, isAllIn, pointAllIn, pointBasisKnown, pointQty } from '../shared/providers.mjs';

export const median = (nums) => {
  const s = [...nums].sort((a, b) => a - b);
  if (!s.length) return null;
  const m = s.length % 2 ? s[(s.length - 1) / 2] : (s[s.length / 2 - 1] + s[s.length / 2]) / 2;
  return +m.toFixed(2);
};

/** ISO date arithmetic without Date-parsing traps around DST. */
export const isoAdd = (iso, n) => {
  const d = new Date(iso + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
export const daysBetween = (a, b) => Math.round((Date.parse(b + 'T12:00:00Z') - Date.parse(a + 'T12:00:00Z')) / 864e5);

const round2 = (n) => Math.round(n * 100) / 100;

/** days of the last 30 that must have a floor before a median is called a "30-day" median */
export const FULL30_DAYS = 25;

/**
 * Every trusted reading of every marketplace, by day, with the day's phantoms flagged.
 * @param entry  one event's tape: { source: [[date, price, allIn?, qty?], ...] }
 * @returns Map<date, { source, price, comp, allIn, known, qty, flagged }[]>
 */
export function dayReadings(entry, today, trusted = () => true, opts = {}) {
  const standIns = opts.standIns;
  const byDay = new Map();
  if (!entry) return byDay;
  for (const [source, pts] of Object.entries(entry)) {
    if (!Array.isArray(pts)) continue;
    const seen = new Set();
    for (const pt of pts) {
      const d = pt[0];
      const p = pt[1];
      if (!(typeof p === 'number' && p > 0) || typeof d !== 'string' || d > today || !trusted(source, d)) continue;
      const row = {
        source,
        price: p,
        comp: comparablePrice(pt, source),
        allIn: pointAllIn(pt, source),
        known: pointBasisKnown(pt, source),
        qty: pointQty(pt),
        flagged: false,
        // a known broker stand-in price (shared/providers.mjs standInKeys / standInSet): unconfirmed, left out of every statistic
        standIn: !!standIns && standIns.has(`${source}|${Math.round(p * 100)}`),
      };
      const list = byDay.get(d) ?? [];
      // one reading per marketplace per day (the last one wins, as a same-day rebuild would)
      const dup = seen.has(`${source}|${d}`) ? list.findIndex((r) => r.source === source) : -1;
      if (dup >= 0) list[dup] = row;
      else list.push(row);
      seen.add(`${source}|${d}`);
      byDay.set(d, list);
    }
  }
  for (const rows of byDay.values()) {
    const flags = dayFlags(rows);
    rows.forEach((r, i) => {
      r.flagged = flags[i];
    });
  }
  return byDay;
}

/**
 * The day's floor: the cheapest comparable unflagged price (ties: fees included first, then the
 * marketplace's name), or null when every reading of the day is flagged.
 */
const floorOf = (rows) => {
  let best = null;
  for (const r of rows) {
    if (r.flagged) continue;
    if (!best || r.comp < best.comp || (r.comp === best.comp && ((r.allIn && !best.allIn) || (r.allIn === best.allIn && r.source < best.source)))) best = r;
  }
  return best && { price: best.comp, raw: best.price, source: best.source, allIn: best.allIn, est: !best.allIn, qty: best.qty };
};

/**
 * The event's daily floors, oldest first: [{ date, price, raw, source, allIn, est, qty }].
 * THE series — the summary, the catalog's trend, the day change and the World Series page read it.
 */
export function floorSeries(entry, today, trusted = () => true, opts = {}) {
  const out = [];
  for (const [date, rows] of [...dayReadings(entry, today, trusted, opts)].sort((a, b) => (a[0] < b[0] ? -1 : 1))) {
    const f = floorOf(rows);
    if (f) out.push({ date, ...f });
  }
  return out;
}

/**
 * Yesterday's floor to today's, when they are comparable: both exist (the build day and the calendar
 * day before it), both fees-included readings (`all-in`) or both estimates (`est`), and both for the
 * same number of tickets. Otherwise null — never 0, which a page would print as "flat today".
 * @returns {{ change: number, basis: 'all-in' | 'est', now: number, before: number } | null}
 */
export function dayMove(entry, today, trusted = () => true, opts = {}) {
  const f = floorSeries(entry, today, trusted, opts);
  const now = f[f.length - 1];
  const before = f[f.length - 2];
  if (!now || !before || now.date !== today || before.date !== isoAdd(today, -1)) return null;
  if (now.allIn !== before.allIn || now.qty !== before.qty) return null;
  return { change: round2(now.price - before.price), basis: now.allIn ? 'all-in' : 'est', now: now.price, before: before.price };
}

/**
 * The cheapest FEES-INCLUDED floor of each of the last  calendar days (today counts as one),
 * oldest first: [[date, price]]. The World Series page's 14 days (scripts/gen-insights.mjs) — THE
 * series of the event pages (floorSeries), minus the days whose floor is only an estimate (a price
 * before fees times its fee wedge), because that page says "fees included".
 */
export const allInFloors = (entry, today, trusted = () => true, days = 14, opts = {}) => {
  const from = isoAdd(today, -(days - 1));
  return floorSeries(entry, today, trusted, opts)
    .filter((f) => f.allIn && f.date >= from)
    .map((f) => [f.date, f.price]);
};

/**
 * The numbers a card shows, from the series AND the event's headline. The card prints the headline
 * (the best offer), so its trend and its change follow it, not whichever offer set the day's cheapest
 * floor: when a fees-included headline is within 1% (or 50 cents) of today's floor it IS the last trend
 * point, and the change is that price against yesterday's floor; otherwise there is no day change (a
 * drop on a price that did not move is worse than none). dayChange is absent unless both days'
 * floors are fees-included readings for the same quantity (dayMove).
 * @param headline { price: number | null | undefined, allIn: boolean | undefined } the event's lowestPrice and lowestAllIn
 * @returns { trend: number[], dayChange?: number }
 */
export function cardNumbers(entry, today, trusted = () => true, headline = {}, opts = {}) {
  const trend = trendOf(entry, today, trusted, 14, opts);
  const move = dayMove(entry, today, trusted, opts);
  const price = headline.price;
  const isFloor = !!move && headline.allIn === true && typeof price === 'number' && Math.abs(move.now - price) <= Math.max(0.5, price * 0.01);
  if (isFloor && trend.length) trend[trend.length - 1] = price;
  return { trend, ...(isFloor && move.basis === 'all-in' ? { dayChange: round2(price - move.before) } : {}) };
}

/** The last `n` recorded days' floors (comparable prices), oldest first: the catalog's sparkline. */
export const trendOf = (entry, today, trusted = () => true, n = 14, opts = {}) => floorSeries(entry, today, trusted, opts).slice(-n).map((f) => f.price);

/** A table cell's mark: '.' = as its marketplace's rule says; '1' = fees included against the rule;
 *  '0' = before fees against the rule; 'x' = unconfirmed (that day's phantom). */
const markOf = (r) => (r.flagged ? 'x' : r.allIn === isAllIn({ source: r.source }) ? '.' : r.allIn ? '1' : '0');

/**
 * What a table cell is, from its mark: whether its price includes fees, and whether it is
 * unconfirmed (left out of every statistic).
 * @param {string | undefined} mark one character of TapeSummary.marks
 * @param {string} source the column's marketplace
 */
export const cellBasis = (mark, source) => ({
  flagged: mark === 'x',
  allIn: mark === '1' ? true : mark === '0' ? false : isAllIn({ source }),
});

/**
 * The index (0-based, among the row's prices) of the day's cheapest comparable unconfirmed cell, or
 * -1: the one a table bolds. @param {(string|number|null)[]} row [date, price per column…]
 * @param {string[]} cols @param {string | undefined} marks the summary's marks @param {number} rowIndex
 */
export const rowFloorIndex = (row, cols, marks, rowIndex) => {
  let at = -1;
  let best = Infinity;
  cols.forEach((c, j) => {
    const v = row[j + 1];
    if (typeof v !== 'number' || !(v > 0)) return;
    const { flagged, allIn } = cellBasis(marks?.[rowIndex * cols.length + j], c);
    if (flagged) return;
    const comp = allIn ? v : round2(v * feeWedge(c));
    if (comp < best) {
      best = comp;
      at = j;
    }
  });
  return at;
};

/**
 * @param entry  one event's tape: { source: [[date, price, allIn?, qty?], ...] }
 * @param today  YYYY-MM-DD (the build day)
 * @param trusted (source, date) => boolean
 * @returns null when there is no usable observation, else
 *   { since, days, last: [price, date, source], low: [price, date, source], high: [price, date],
 *     med30, low30: [price, date, source] | null, range30: [low, high] | null, n30, full30,
 *     wk: { change, pct, span } | null, est: { last, low, high, low30, high30, med30, wkFrom },
 *     cols: [source, ...], rows: [[date, price|null, ...], ...] newest first, last 30 days,
 *     marks?: one character per row cell (cellBasis) }
 *   Every price in `last`/`low`/`high`/`med30`/`low30`/`range30`/`wk` is a comparable, unflagged
 *   floor; `est.*` is true when it came from a price before fees times its fee wedge. `since` and
 *   `days` count the days we recorded a trusted reading (recorded days, not the calendar span);
 *   `n30` the days of the 30-day window that have a floor; `full30` is true when we have recorded
 *   for the whole month (the first reading is at least 29 days before the build day AND at least
 *   FULL30_DAYS of the 30 days have a floor).
 */
export function tapeSummary(entry, today, trusted = () => true, opts = {}) {
  if (!entry) return null;
  const readings = dayReadings(entry, today, trusted, opts);
  const allDays = [...readings.keys()].sort();
  if (!allDays.length) return null;
  const floors = new Map(); // date -> floor
  for (const d of allDays) {
    const f = floorOf(readings.get(d));
    if (f) floors.set(d, f);
  }
  const days = allDays.filter((d) => floors.has(d));
  if (!days.length) return null;

  let low = null;
  let high = null;
  for (const d of days) {
    const f = floors.get(d);
    if (!low || f.price < low.f.price) low = { d, f }; // strict: the earliest day keeps a tie
    if (!high || f.price > high.f.price) high = { d, f };
  }

  // The 30-day window ends on the build day (today counts as day 1 of 30).
  const from30 = isoAdd(today, -29);
  const win = days.filter((d) => d >= from30);
  const med30 = win.length >= 5 ? median(win.map((d) => floors.get(d).price)) : null;
  let low30 = null;
  let hi30 = null;
  for (const d of win) {
    const f = floors.get(d);
    if (!low30 || f.price < low30.f.price) low30 = { d, f };
    if (!hi30 || f.price > hi30.f.price) hi30 = { d, f };
  }
  // the median is an estimate when the floor(s) it is made of are
  let estMed = false;
  if (med30 !== null) {
    const ranked = win.map((d) => floors.get(d)).sort((a, b) => a.price - b.price);
    const mid = ranked.length % 2 ? [ranked[(ranked.length - 1) / 2]] : [ranked[ranked.length / 2 - 1], ranked[ranked.length / 2]];
    estMed = mid.some((f) => f.est);
  }

  // 7-day move: the latest floor against the recording ~7 days earlier (or the earliest one).
  // Only between floors for the same number of tickets; fees-included or estimated, said by est.wk.
  const lastDay = days[days.length - 1];
  const last = floors.get(lastDay);
  let wk = null;
  let estWkFrom = false;
  if (days.length >= 2) {
    const ref = [...days].reverse().find((d) => daysBetween(d, lastDay) >= 7) ?? days[0];
    const r = floors.get(ref);
    if (r.qty === last.qty) {
      const change = round2(last.price - r.price);
      wk = { change, pct: r.price ? Math.round((change / r.price) * 100) : 0, span: Math.max(1, daysBetween(ref, lastDay)) };
      estWkFrom = r.est;
    }
  }

  // Marketplace columns, cheapest-today first (then by name) — the table reads like the board.
  // The table shows every marketplace's OWN price (unconfirmed ones marked), not the floor.
  const bySource = new Map();
  for (const [d, rows] of readings) for (const r of rows) (bySource.get(r.source) ?? bySource.set(r.source, new Map()).get(r.source)).set(d, r);
  const lastReading = allDays[allDays.length - 1];
  const cols = [...bySource.keys()].sort((a, b) => {
    const pa = bySource.get(a).get(lastReading)?.price ?? Infinity;
    const pb = bySource.get(b).get(lastReading)?.price ?? Infinity;
    return pa - pb || a.localeCompare(b);
  });
  const tableDays = allDays.filter((d) => d >= from30).reverse();
  const rows = tableDays.map((d) => [d, ...cols.map((c) => bySource.get(c).get(d)?.price ?? null)]);
  const marks = tableDays.map((d) => cols.map((c) => (bySource.get(c).has(d) ? markOf(bySource.get(c).get(d)) : '.')).join('')).join('');

  return {
    since: allDays[0],
    days: allDays.length,
    last: [last.price, lastDay, last.source],
    low: [low.f.price, low.d, low.f.source],
    high: [high.f.price, high.d],
    med30,
    low30: low30 && [low30.f.price, low30.d, low30.f.source],
    range30: low30 && [low30.f.price, hi30.f.price],
    n30: win.length,
    // "30-day" is said only when we really have a month: recording started 29+ days ago AND at least
    // FULL30_DAYS of the 30 days have a floor (377 tapes said "30-day median" on under 20 days)
    full30: allDays[0] <= from30 && win.length >= FULL30_DAYS,
    wk,
    est: { last: last.est, low: low.f.est, high: high.f.est, low30: !!low30?.f.est, high30: !!hi30?.f.est, med30: estMed, wkFrom: estWkFrom },
    cols,
    rows,
    ...(/[^.]/.test(marks) ? { marks } : {}),
  };
}
